import { observable, action, computed, reaction, toJS, makeObservable } from 'mobx';
import moment from 'moment';
import _ from 'lodash';
import {
  getPersistedValue,
  getSessionPersistedValue,
  setPersistedValue,
  removePersistedValue,
} from 'shared/utils/persisted-values';

import { formatName } from 'shared/helpers/users';
import { mapBankAccounts } from 'src/payments/utils';
import { isRelinkOrEditBankAccountEnabled } from 'src/payments/utils/parse-redirect-url';
import { StatusTypes } from 'src/gql/graphql';
import type {
  IdentityConsumerProfile,
  UserEmails,
  ConsumerUserFragmentFragment,
  ShowEnrollmentQuery,
  ConsumerUserMedicalCard,
} from 'src/gql/graphql';
import DutchiePayEnrollment, { EnrollmentBankAccount } from 'src/state/dutchiePay-enrollment';

type UserProfile = IdentityConsumerProfile & { driversLicense?: string };

type AgeOverride = {
  expires: string;
  age: number;
};

type ExternalUserDetails = {
  birthDay: string;
  birthMonth: string;
  birthYear: string;
  email: string;
  externalUserId: string;
  firstName: string;
  lastName: string;
  phone: string;
};

type LoginData = {
  user: ConsumerUserFragmentFragment;
  accessToken: string | null;
};

// store data about the current user, guest or authed, in this store
// also see the controller inside of hooks/use-user-controller

// there are distinctly 3 logged in states, not 2
// - Not logged in, guest experience
// - Logged in, has profile and stuff
// - *Logging in*, has no user data but isn't a guest
export default class UserStore {
  @observable id: string | null = null;
  @observable profile: UserProfile = {};
  @observable email: string | null = null;
  @observable emails: Array<UserEmails | null> = [];
  @observable token: string | null = null;
  @observable createdAt: string | null = null;
  @observable viewOrderId: string | null = null;
  @observable refetchRequest = false;
  @observable ageOverride: AgeOverride | null = null;
  @observable loading: boolean | null = null;
  @observable userDetailsFromExternalSource: Partial<ExternalUserDetails> = {};
  @observable hasLoyaltyAccount = false;
  @observable isPublicSessionPhoneNumberLogin = false;
  @observable kioskGuestPhoneNumber: string | null = null;

  // Account Verification
  @observable hasAttemptedVerification = false;
  @observable isVerificationTokenSent = false;
  @observable activeVerificationToken: string | null = null;
  @observable lastVerifiedAt: string | null = null;

  /**
   * Dutchie Pay enrollment status for the current user
   */
  @observable payByBankEnrollmentStatus: StatusTypes = StatusTypes.statusUnenrolled;
  /**
   * Array of bank accounts for the current user
   */
  @observable payByBankBankAccounts: EnrollmentBankAccount[] = [];

  dutchiePayEnrollmentStore: DutchiePayEnrollment;

  constructor(dutchiePayEnrollmentStore: DutchiePayEnrollment) {
    makeObservable(this);
    reaction(
      () => (this.emails.length > 0 ? this.emails[0]?.address : null),
      (email) => {
        if (email) {
          this.email = email;
        }
      }
    );
    this.dutchiePayEnrollmentStore = dutchiePayEnrollmentStore;
    this.ageOverride = getPersistedValue('ageOverride', null);
    this.token = getPersistedValue('access-token', null);
    this.activeVerificationToken = getPersistedValue('verification-token', null);
    this.isPublicSessionPhoneNumberLogin = getPersistedValue('isPublicSessionPhoneNumberLogin', false);
  }

  @action
  setAgeOverride(age: number, rememberMe: boolean, forceSessionStorage = false): void {
    const expireDays = rememberMe ? 30 : 1;
    this.ageOverride = {
      expires: moment().add(expireDays, 'days').toISOString(),
      age,
    };

    // Use session storage if forceSessionStorage is true (e.g., Maryland) or rememberMe is false
    const sessionOnly = forceSessionStorage || !rememberMe;
    setPersistedValue('ageOverride', this.ageOverride, sessionOnly);
  }

  /**
   * Gets age override from session storage only (ignores localStorage).
   * Used when compliance requires session-only storage (e.g., Maryland).
   */
  getSessionOnlyAgeOverride(): AgeOverride | null {
    return getSessionPersistedValue('ageOverride', null);
  }

  @computed
  get isLoggedIn(): boolean {
    return !!this.id;
  }

  @computed
  get exists(): boolean {
    return this.isLoggedIn;
  }

  @action
  login(loginData: LoginData): void {
    this.setUser(loginData.user);
    this.updateToken(loginData.accessToken);
  }

  @action
  updateToken(token: string | null): void {
    this.token = token;
    setPersistedValue(`access-token`, token);
  }

  // Because of the way the user store was built and integrated, we have to
  // manually update the user object manually after mutations
  @action
  refetch(): void {
    this.refetchRequest = true;
  }

  @action
  logout(): void {
    this.token = null;
    this.id = null;
    this.createdAt = null;
    this.profile = {};
    this.emails = [];
    this.email = null;
    this.activeVerificationToken = null;
    removePersistedValue(`loyaltyPins`);
    removePersistedValue(`verification-token`);
    removePersistedValue(`isPublicSessionPhoneNumberLogin`);
    this.hasLoyaltyAccount = false;
    this.isPublicSessionPhoneNumberLogin = false;
    this.kioskGuestPhoneNumber = null;
    this.resetUserPayByBankEnrollmentStatus();
  }

  @action
  setUser(userData: ConsumerUserFragmentFragment): void {
    this.id = userData._id;
    this.profile = userData.profile;
    this.emails = userData.emails;
    this.createdAt = userData.createdAt;
    this.lastVerifiedAt = userData.emails[0]?.lastVerifiedAt ?? null;
    // keeping lint rule for now, we can remove it after useStores / useUser are migrated to ts
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (userData?.payByBankEnrollmentStatus) {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const enrollmentStatus = userData.payByBankEnrollmentStatus.enrollmentStatus ?? StatusTypes.statusUnenrolled;
      const relinkOrEditBankAccountEnabled = isRelinkOrEditBankAccountEnabled();
      this.setUserPayByBankEnrollmentStatus({
        enrollmentStatus,
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        paymentMethods: userData.payByBankEnrollmentStatus.paymentMethods ?? [],
        relinkOrEditBankAccountEnabled,
      });
    }
  }

  /**
   * Sets the user's Pay by Bank enrollment status and payment methods
   */
  @action
  setUserPayByBankEnrollmentStatus({
    enrollmentStatus,
    paymentMethods,
    relinkOrEditBankAccountEnabled = false,
    completeEnrollment = false,
  }: {
    enrollmentStatus: StatusTypes;
    paymentMethods: ShowEnrollmentQuery['showEnrollment']['paymentMethods'];
    relinkOrEditBankAccountEnabled?: boolean;
    completeEnrollment?: boolean;
  }): void {
    const bankAccounts = mapBankAccounts(paymentMethods);
    this.payByBankEnrollmentStatus = enrollmentStatus;
    this.payByBankBankAccounts = bankAccounts;
    this.dutchiePayEnrollmentStore.enrollmentStatusReceived({
      enrollmentStatus,
      relinkOrEditBankAccountEnabled,
      completeEnrollment,
    });
  }

  @action resetUserPayByBankEnrollmentStatus(): void {
    this.payByBankEnrollmentStatus = StatusTypes.statusUnenrolled;
    this.payByBankBankAccounts = [];
    this.dutchiePayEnrollmentStore.resetEnrollment();
  }

  @action
  setIsPublicSessionPhoneNumberLogin(value: boolean): void {
    this.isPublicSessionPhoneNumberLogin = value;
    setPersistedValue('isPublicSessionPhoneNumberLogin', value, true);
  }

  @action
  setKioskGuestPhoneNumber(phoneNumber: string | null): void {
    this.kioskGuestPhoneNumber = phoneNumber;
  }

  @action
  setUserDetailsFromExternalSource(userDetails: ExternalUserDetails): void {
    // TODO: we should probably validate the user details in some way?
    this.userDetailsFromExternalSource = userDetails;
  }

  @action
  removeUserDetailsFromExternalSource(): void {
    this.userDetailsFromExternalSource = {};
  }

  @action
  setMedicalCard = (medicalCard: Partial<ConsumerUserMedicalCard>): void => {
    _.set(this.profile, `medicalCard`, {
      ...(this.profile.medicalCard || {}),
      ..._.omitBy(medicalCard, _.isEmpty),
    });
  };

  @action
  setLoyaltyPin = (dispensaryId: string, pin: string): void => {
    const existingLoyaltyPins = getPersistedValue(`loyaltyPins`, {});

    existingLoyaltyPins[dispensaryId] = pin;

    setPersistedValue(`loyaltyPins`, existingLoyaltyPins);
  };

  getLoyaltyPin = (dispensaryId: string): string | null => {
    const existingLoyalty = getPersistedValue(`loyaltyPins`, {});

    return existingLoyalty[dispensaryId] ?? null;
  };

  @computed get hasExternalUser(): boolean {
    return !_.isEmpty(this.userDetailsFromExternalSource);
  }

  @computed get formatedBirthdate(): string {
    if (!this.isLoggedIn) {
      return '';
    }
    return moment(this.profile.birthday, 'M/D/YYYY', 'MM/DD/YYYY').format('MM/DD/YYYY');
  }

  @computed get birthday(): string {
    return this.profile.birthday ?? '';
  }

  @computed get phone(): string | null {
    return this.profile.phone ?? null;
  }

  @computed get firstName(): string {
    if (!this.exists) {
      return '';
    }
    return _.get(this.profile, 'firstName', _.split(_.get(this.profile, 'fullName', ''), ' ')[0]) ?? '';
  }

  @computed get lastName(): string {
    if (!this.exists) {
      return '';
    }
    return _.get(this.profile, 'lastName', _.split(_.get(this.profile, 'fullName', ''), ' ')[1]) ?? '';
  }

  @computed get fullName(): string {
    if (!this.exists) {
      return '';
    }
    return formatName(this.profile);
  }

  @computed get user(): UserStore {
    return toJS(this);
  }

  @computed get isSsoUser(): boolean {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return this.profile?.isExternalUser ?? false;
  }

  decodeJWT(token: string): { [key: string]: unknown; exp: number } {
    const parts = token.split('.');

    if (parts.length !== 3) {
      throw new Error('Invalid JWT token');
    }

    // Decode the payload part (2nd part) from base64 URL encoding
    const payload = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(payload);
  }

  @computed get expiredToken(): boolean {
    if (!this.token) {
      return true;
    }

    const decodedToken = this.decodeJWT(this.token);
    const now = Math.floor(Date.now() / 1000);
    return now > decodedToken.exp;
  }
}
